import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { StorefrontThemeContext } from '@/features/storefront/storefrontThemeContext';
import { themeLayoutSchema, type ThemeLayout } from '@/features/storefront/themeSchema';

/** What an unstyled moment renders as: the base theme, every area on it. */
const DEFAULT_LAYOUT: ThemeLayout = themeLayoutSchema.parse({});

/**
 * The layout choices of the theme the shell is rendering.
 *
 * The shell's own theme first - which in preview is the theme being tried on,
 * not the published one - and the published context otherwise, from the query
 * the shell already fetched (a cache read in practice, never a second
 * request). Until either arrives this reports the base defaults, which is also
 * what the shell is showing at that moment.
 */
export function useThemeLayout(): ThemeLayout {
  const shellTheme = useContext(StorefrontThemeContext);
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const context = useQuery({
    queryKey: [...scope, 'context'],
    queryFn: () => source.context(),
    enabled: shellTheme === null,
  });

  return shellTheme?.layout ?? context.data?.theme.layout ?? DEFAULT_LAYOUT;
}
